// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  RatingType,
} from 'common.data';
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import moment from 'moment';
import {
  cleanFormValues,
} from '@vroskus/library-helpers/dist/browser';
import {
  defaultGetData,
  defaultUpsertItem,
  replaceObjectCommasToDots,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import RatingFields from '../../fields/RatingFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate & $WithRouter;

export type $StateProps = Record<string, never>;

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'Rating'>>;
  loading: boolean;
  mainItem: $Model<'Entry'> | null;
};

const pageId = adminPages.adminEntryRatingsPage;

class AdminEntryRatingsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      mainItem: null,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          mainItem,
        } = this.state;

        if (mainItem !== null && data.RegionId === mainItem.RegionId && data.modelName === 'Rating') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      params,
    } = this.props;

    const {
      EntryId,
    } = params;

    await defaultGetData({
      action: async () => {
        if (EntryId) {
          await this.setStateAsync({
            loading: true,
          });

          const mainItem = await Services.Api.getOneAdminEntry({
            id: EntryId,
          });
          const data = await Services.Api.getManyAdminEntryRating({
            EntryId,
          });

          await this.setStateAsync({
            data,
            loading: false,
            mainItem,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      redirect,
      t,
    } = this.props;
    const {
      mainItem,
    } = this.state;

    if (mainItem !== null) {
      const newItem: $ModelUpsertParams<'Rating'> = {
        File: null,
        Latest: true,
        ReferenceId: null,
        RegionId: mainItem.RegionId,
        RegistrationNo: mainItem.RegistrationNo,
        Type: RatingType.monotype,
        Uid: String(moment().unix()),
        Values: {
        },
      };

      return (
        <Card id={adminSections[pageId].header}>
          <SectionHeader
            description={mainItem !== null ? `${mainItem.RegistrationNo} ${mainItem.Name || ''}` : ''}
            rightButtons={(
              <FormDialog
                fields={(
                  <RatingFields item={newItem} />
                )}
                id={'create-rating-dialog'}
                labelCloseButton={t('Close')}
                labelSaveButton={t('Save')}
                onAction={(values) => defaultUpsertItem({
                  action: async () => {
                    const params = cleanFormValues<$Model<'Rating'>>({
                      ...newItem,
                      ...values,
                      Values: replaceObjectCommasToDots(values.Values as Record<string, string>),
                    });

                    const response = await Services.Api.createRating(params);

                    await Services.Api.addAdminEntryRating({
                      EntryId: mainItem.id,
                      RatingId: response.id,
                    });
                  },
                })}
                subTitle={t('New')}
                title={t('Entry rating')}
              >
                <Button
                  color={'info'}
                  id={'create-rating-button'}
                  size={'sm'}
                  title={t('Add new rating')}
                >
                  {t('Add new')}
                </Button>
              </FormDialog>
            )}
            title={t('Entry ratings')}
            titleButton={(
              <Button
                className={'mr-2'}
                color={'secondary'}
                id={'redirect-admin-entries-page-button'}
                onClick={() => redirect({
                  pathname: '/admin/entries',
                })}
                size={'sm'}
              >
                <em className={'fa icon-arrow-left'} />
              </Button>
            )}
          />
        </Card>
      );
    }

    return null;
  }

  renderButtons(dataItem: $Model<'Rating'>) {
    const {
      t,
    } = this.props;
    const {
      mainItem,
    } = this.state;

    if (mainItem !== null) {
      const successorItem: $ModelUpsertParams<'Rating'> = {
        File: null,
        Latest: true,
        ReferenceId: dataItem.id,
        RegionId: mainItem.RegionId,
        RegistrationNo: mainItem.RegistrationNo,
        Type: dataItem.Type,
        Uid: String(moment().unix()),
        Values: dataItem.Values,
      };

      const entryName: string = mainItem !== null
        ? `${mainItem.RegistrationNo} ${mainItem.Name || ''}`
        : '';

      return (
        <div className={'d-flex justify-content-end'}>
          <FormDialog
            fields={(
              <RatingFields item={successorItem} />
            )}
            id={`update-rating-dialog-${dataItem.id}`}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'Rating'>>({
                  ...successorItem,
                  ...values,
                  Values: replaceObjectCommasToDots(values.Values as Record<string, string>),
                });

                const response = await Services.Api.createRating(params);

                await Services.Api.addAdminEntryRating({
                  EntryId: mainItem.id,
                  RatingId: response.id,
                });
              },
            })}
            subTitle={`${dataItem.Type}: ${entryName}`}
            title={t('Entry rating')}
          >
            <Button
              color={'warning'}
              id={`update-rating-button-${dataItem.id}`}
              outline
              size={'sm'}
            >
              {t('Edit')}
            </Button>
          </FormDialog>
        </div>
      );
    }

    return null;
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Type')}</th>
            <th>{t('Value')}</th>
            <th>{t('Date')}</th>
            <th className={'text-center'}>{t('Latest')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'Rating'>) => (
            <tr key={dataItem.id}>
              <td>{dataItem.Type}</td>
              <td>{`${dataItem.KeyValueName}: ${dataItem.KeyValue || ''}`}</td>
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td className={'text-center'}>
                {dataItem.Latest === true && (
                  <em className={'icon-check'} />
                )}
              </td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminEntryRatingsPageView;
